import {
  MarketingTriggerKey,
  OrderTrackingTriggerKey,
} from "@redotech/redo-flows-common/triggers";
import * as z from "zod";
import { SkillId } from "./skill-id";

/**
 * Playbook UI taxonomy. Each trigger belongs to exactly one category, which
 * drives how it's grouped in the merchant-facing playbook tree. Unrelated to
 * routing — see TriggerConversationType for that.
 */
export enum TriggerCategory {
  CART_CHECKOUT = "cart_checkout",
  SIGNUPS = "signups",
  PRODUCT_INTEREST = "product_interest",
  RETURNS_WARRANTY = "returns_warranty",
  ORDER_LIFECYCLE = "order_lifecycle",
  SHIPPING_UPDATES = "shipping_updates",
  SHIPPING_ISSUES = "shipping_issues",
  PICKUP = "pickup",
}

/**
 * Categories that happen after a purchase — used by the Text Concierge product
 * and landing page to scope "post-purchase" experiences. Pre-purchase is the
 * complement.
 */
export const POST_PURCHASE_CATEGORIES: readonly TriggerCategory[] = [
  TriggerCategory.RETURNS_WARRANTY,
  TriggerCategory.ORDER_LIFECYCLE,
  TriggerCategory.SHIPPING_UPDATES,
  TriggerCategory.SHIPPING_ISSUES,
  TriggerCategory.PICKUP,
];

export const PRE_PURCHASE_CATEGORIES: readonly TriggerCategory[] = [
  TriggerCategory.CART_CHECKOUT,
  TriggerCategory.SIGNUPS,
  TriggerCategory.PRODUCT_INTEREST,
];

/**
 * Which product handles conversations started by this trigger.
 * - CONCIERGE: advanced-flow concierge step — reactive, conversational support.
 * - MARKETING: vanguard loop + marketing-agent coordinator — proactive outreach.
 *
 * String values mirror MarketingConversationType in redo-marketing-common so
 * the temporal activity can cast at the package boundary without a lookup.
 */
export enum TriggerConversationType {
  CONCIERGE = "CONCIERGE",
  MARKETING = "MARKETING",
}

/** Analytics discriminator on shopper_agent_insights; uses the current product name for the reactive category. */
export enum ConversationInsightType {
  COMMERCE_AGENT = "COMMERCE_AGENT",
  MARKETING = "MARKETING",
}

/** Maps the routing category to its analytics label; the legacy CONCIERGE routing value is Commerce Agent today. */
export function conversationInsightTypeFromTrigger(
  type: TriggerConversationType,
): ConversationInsightType {
  return type === TriggerConversationType.MARKETING
    ? ConversationInsightType.MARKETING
    : ConversationInsightType.COMMERCE_AGENT;
}

export const TriggerKeySchema = z.enum([
  ...Object.values(MarketingTriggerKey),
  ...Object.values(OrderTrackingTriggerKey),
]);

export type TriggerKey = z.infer<typeof TriggerKeySchema>;

export const TriggerDefinitionSchema = z.object({
  id: TriggerKeySchema,
  name: z.string(),
  description: z.string(),
  /**
   * Event timing bucket (pre- vs post-purchase). Unsupported events have
   * no TriggerDefinition and are listed in UNSUPPORTED_TRIGGERS.
   */
  category: z.enum(TriggerCategory),
  /** Routing decision — which product handles conversations from this trigger. */
  conversationType: z.enum(TriggerConversationType),
  defaultEnabled: z.boolean(),
  /** Skills this trigger can invoke */
  skills: z.array(z.enum(SkillId)),
  /** What this trigger means for the customer — context the agents (mainly the writer) get when it fires */
  guidance: z.string().min(1),
  /** When to send this trigger's outreach and when to hold — the sentinel's default decision */
  sendPolicyGuidance: z.string().min(1),
  /**
   * Coordinator buffer and Sentinel defaults for this event.
   */
  processingPolicy: z
    .object({
      buffer: z.object({ defaultBypass: z.boolean().optional() }).optional(),
      sentinel: z.object({ defaultBypass: z.boolean().optional() }).optional(),
    })
    .optional(),
  /**
   * Opts SMS outreach into quiet hours, allowed only for a prompt, purely transactional message.
   * `transactionalGuidance` is what that message is for.
   */
  sendDuringQuietHours: z
    .object({ transactionalGuidance: z.string().min(1) })
    .optional(),
});

export type TriggerDefinition = z.infer<typeof TriggerDefinitionSchema>;
